<template>
  <draggable-panel
    :visible="visible"
    :title="`船舶航迹 - ${shipName}`"
    icon="el-icon-location-information"
    :width="700"
    :initial-position="{ top: 40, right: 10 }"
    :z-index="1000"
    @close="closeViewer"
  >
    <div class="track-viewer-container">
      <div class="track-viewer-toolbar">
        <el-date-picker
          v-model="localTimeRange"
          type="datetimerange"
          range-separator="至"
          start-placeholder="开始时间"
          end-placeholder="结束时间"
          value-format="yyyy-MM-dd HH:mm:ss"
          :default-time="['00:00:00', '23:59:59']"
          :picker-options="pickerOptions"
          size="small"
          style="width: 350px;"
          @change="onTimeRangeChanged">
        </el-date-picker>
        <el-button type="primary" size="mini" @click="loadTrackData">刷新</el-button>
        <el-button type="info" size="mini" @click="toggleDataTable">{{ showDataTable ? '隐藏数据' : '显示数据' }}</el-button>
        <el-button type="warning" size="mini" @click="toggleLabels">{{ forceShowLabels ? '隐藏标签' : '显示标签' }}</el-button>
      </div>
      
      <div class="track-viewer-content">
        <div v-if="loading" class="loading">
          <i class="el-icon-loading"></i>
          <span>加载中...</span>
        </div>
        <div v-else-if="trackPoints.length === 0" class="empty-data">
          <i class="el-icon-warning"></i>
          <span>该时间段内暂无航迹数据</span>
        </div>
        <div v-else class="track-info">
          <div class="track-summary">
            <div class="summary-item">
              <span class="label">数据点数:</span>
              <span class="value">{{ trackPoints.length }}</span>
            </div>
            <div class="summary-item">
              <span class="label">平均速度:</span>
              <span class="value">{{ avgSpeed }} 节</span>
            </div>
            <div class="summary-item">
              <span class="label">最高速度:</span>
              <span class="value">{{ maxSpeed }} 节</span>
            </div>
          </div>
        </div>
        
        <!-- 轨迹详细数据表格 -->
        <div v-if="showDataTable" class="data-table-container">
          <trajectory-data-table
            :trajectoryData="trackPoints"
            :loading="loading"
            :totalCount="trackPoints.length"
            :currentPage="dataTableParams.pageNum"
            :pageSize="dataTableParams.pageSize"
            @pagination-change="handlePaginationChange"
          ></trajectory-data-table>
        </div>
      </div>
    </div>
  </draggable-panel>
</template>

<script>
import { searchVesselTrajectory } from '@/api/vessel-trajectory'
import L from 'leaflet'
import 'leaflet-polylinedecorator' // 引入箭头装饰器插件
import TrajectoryDataTable from './TrajectoryDataTable.vue'
import * as turf from '@turf/turf' // 引入turf.js用于地理空间计算
import DraggablePanel from '@/components/common/DraggablePanel.vue'

export default {
  name: 'ShipTrackViewer',
  components: {
    TrajectoryDataTable,
    DraggablePanel
  },
  props: {
    visible: {
      type: Boolean,
      default: false
    },
    mmsi: {
      type: String,
      required: false,
      default: ''
    },
    map: {
      type: Object,
      required: true
    },
    timeRange: {
      type: Array,
      required: false,
      default: () => {
        const endTime = new Date();
        const startTime = new Date(endTime.getTime() - 24 * 60 * 60 * 1000); // 默认24小时
        return [
          startTime.toISOString().split('T')[0] + ' ' + startTime.toTimeString().split(' ')[0],
          endTime.toISOString().split('T')[0] + ' ' + endTime.toTimeString().split(' ')[0]
        ];
      }
    },
    criticalAreas: {
      type: Array,
      required: false,
      default: () => []
    }
  },
  data() {
    return {
      loading: false,
      localTimeRange: [],
      trackPoints: [],
      shipName: '',
      trackLayer: null,
      showDataTable: false,
      dataTableParams: {
        pageNum: 1,
        pageSize: 10
      },
      latestShipPosition: null, // 存储最新的船舶位置
      forceShowLabels: false, // 是否显示标签（默认不显示）
      pickerOptions: {
        shortcuts: [
          {
            text: '最近1小时',
            onClick(picker) {
              const end = new Date()
              const start = new Date()
              start.setTime(start.getTime() - 3600 * 1000)
              picker.$emit('pick', [start, end])
            }
          },
          {
            text: '最近6小时',
            onClick(picker) {
              const end = new Date()
              const start = new Date()
              start.setTime(start.getTime() - 6 * 3600 * 1000)
              picker.$emit('pick', [start, end])
            }
          },
          {
            text: '最近12小时',
            onClick(picker) {
              const end = new Date()
              const start = new Date()
              start.setTime(start.getTime() - 12 * 3600 * 1000)
              picker.$emit('pick', [start, end])
            }
          },
          {
            text: '最近24小时',
            onClick(picker) {
              const end = new Date()
              const start = new Date()
              start.setTime(start.getTime() - 24 * 3600 * 1000)
              picker.$emit('pick', [start, end])
            }
          }
        ]
      }
    }
  },
  computed: {
    avgSpeed() {
      if (!this.trackPoints.length) return '0.00'
      
      let totalSpeed = 0
      let validSpeedCount = 0
      
      this.trackPoints.forEach(point => {
        if (point.speed !== null && point.speed !== undefined) {
          totalSpeed += point.speed
          validSpeedCount++
        }
      })
      
      return validSpeedCount > 0 ? (totalSpeed / validSpeedCount).toFixed(2) : '0.00'
    },
    maxSpeed() {
      if (!this.trackPoints.length) return '0.00'
      
      let maxSpeed = 0
      
      this.trackPoints.forEach(point => {
        if (point.speed !== null && point.speed !== undefined) {
          maxSpeed = Math.max(maxSpeed, point.speed)
        }
      })
      
      return maxSpeed.toFixed(2)
    }
  },
  mounted() {
    console.log('ShipTrackViewer mounted. MMSI:', this.mmsi, 'TimeRange:', this.timeRange);
    
    // 设置本地时间范围 - 只有在timeRange watcher还没有设置时才设置
    if (!this.localTimeRange || this.localTimeRange.length !== 2) {
      if (this.timeRange && Array.isArray(this.timeRange) && this.timeRange.length === 2) {
        this.localTimeRange = [...this.timeRange];
        console.log('在mounted中使用props时间范围:', this.localTimeRange);
      } else {
        // 设置默认时间范围（1小时）
        const endTime = new Date();
        const startTime = new Date(endTime.getTime() - 60 * 60 * 1000);
        this.localTimeRange = [
          this.formatDateTime(startTime),
          this.formatDateTime(endTime)
        ];
        console.log('在mounted中使用默认时间范围:', this.localTimeRange);
      }
    }
    
    // 设置船舶名称
    if (!this.shipName && this.mmsi) {
      this.shipName = this.mmsi;
    }
    
    // 创建轨迹图层
    this.createTrackLayer();
    
    // 监听地图缩放事件，动态更新标签显示
    if (this.map) {
      this.map.on('zoomend', this.handleMapZoomChange);
    }
  },
  beforeDestroy() {
    console.log('ShipTrackViewer组件销毁，清理资源');
    
    try {
      // 移除地图缩放事件监听
      if (this.map) {
        this.map.off('zoomend', this.handleMapZoomChange);
      }
      
      // 移除轨迹图层
      this.removeTrackLayer();
      
      // 清空数据
      this.trackPoints = [];
      this.loading = false;
      
      // 确保地图对象在组件销毁后仍然有效
      if (this.map) {
        // 延迟执行地图刷新，确保所有相关操作完成
        setTimeout(() => {
          try {
            // 刷新地图布局以修复可能的问题
            this.map.invalidateSize();
          } catch(e) {
            console.warn('地图刷新失败，但不影响使用:', e);
          }
        }, 300);
      }
      
      // 通知父组件组件即将销毁，以便父组件可以清理相关资源
      this.$emit('before-destroy');
    } catch (error) {
      console.error('ShipTrackViewer组件销毁时出错:', error);
    }
  },
  methods: {
    /**
     * 从响应数据加载轨迹数据（由父组件调用）
     * @param {Array} trajectoryData - 处理后的轨迹数据
     */
    loadTrajectoryDataFromResponse(trajectoryData) {
      if (!trajectoryData || !Array.isArray(trajectoryData) || trajectoryData.length === 0) {
        console.warn('轨迹数据无效，无法加载');
        return;
      }
      
      console.log('ShipTrackViewer: 从响应数据加载轨迹数据，条数:', trajectoryData.length);
      
      // 直接使用传入的轨迹数据
      this.trackPoints = trajectoryData;
      
      // 提取船舶名称
      if (this.trackPoints.length > 0 && this.trackPoints[0].vesselName) {
        this.shipName = this.trackPoints[0].vesselName;
      } else {
        this.shipName = this.mmsi;
      }
      
      // 保存最新的船舶位置用于定位功能
      if (this.trackPoints.length > 0) {
        const latestPoint = this.trackPoints[this.trackPoints.length - 1];
        this.latestShipPosition = {
          lat: latestPoint.latitude,
          lng: latestPoint.longitude
        };
      }
      
      // 确保地图图层已创建后再绘制轨迹
      this.$nextTick(() => {
        // 如果图层还没创建，先创建图层
        if (!this.trackLayer && this.map) {
          this.createTrackLayer();
        }
        
        // 绘制航迹
        this.drawTrackOnMap();
        
        console.log('轨迹数据已加载并绘制到地图上，数据点数:', this.trackPoints.length);
      });
      
      // 通知父组件航迹加载完成
      this.$emit('track-loaded', this.trackPoints);
    },
    
    loadTrackData() {
      if (!this.mmsi || this.mmsi.trim() === '') {
        console.warn('未提供有效的船舶MMSI，无法查询航迹');
        return;
      }
      
      if (!this.localTimeRange || this.localTimeRange.length !== 2) {
        console.warn('时间范围无效，设置默认1小时范围', this.localTimeRange);
        const endTime = new Date();
        const startTime = new Date(endTime.getTime() - 60 * 60 * 1000);
        this.localTimeRange = [
          this.formatDateTime(startTime),
          this.formatDateTime(endTime)
        ];
      }
      
      // 如果已经在加载中，避免重复请求
      if (this.loading) {
        console.warn('航迹数据正在加载中，忽略重复请求');
        return;
      }
      
      this.loading = true;
      
      // 使用与ShipTrajectory相同的API接口查询航迹
      const params = {
        keyword: this.mmsi,
        startTime: this.localTimeRange[0],
        endTime: this.localTimeRange[1],
        pageNum: 1,
        pageSize: 1000 // 获取足够多的点来绘制航迹
      };
      
      console.log(`执行API调用: 查询船舶 ${this.mmsi} 航迹数据，参数:`, JSON.stringify(params));
      
      searchVesselTrajectory(params)
        .then(res => {
          if (res.code === 200 && res.data) {
            // 获取原始轨迹点
            let points = res.data.list || [];
            
            // 按时间戳排序 - 从早到晚
            points.sort((a, b) => {
              return new Date(a.timestamp) - new Date(b.timestamp);
            });
            
            this.trackPoints = points;
            
            // 提取船舶名称
            if (this.trackPoints.length > 0 && this.trackPoints[0].vesselName) {
              this.shipName = this.trackPoints[0].vesselName;
            } else {
              this.shipName = this.mmsi;
            }
            
            // 保存最新的船舶位置用于定位功能
            if (this.trackPoints.length > 0) {
              const latestPoint = this.trackPoints[this.trackPoints.length - 1];
              this.latestShipPosition = {
                lat: latestPoint.latitude,
                lng: latestPoint.longitude
              };
            }
            
            // 绘制航迹
            this.drawTrackOnMap();
            
            // 通知父组件航迹加载完成
            this.$emit('track-loaded', this.trackPoints);
          } else {
            this.$message.warning('查询航迹数据失败');
            this.trackPoints = [];
          }
        })
        .catch(error => {
          console.error('查询航迹失败:', error);
          this.$message.error('查询航迹失败，请检查网络连接');
          this.trackPoints = [];
        })
        .finally(() => {
          this.loading = false;
        });
    },
    
    createTrackLayer() {
      // 使用Leaflet创建一个新的图层用于显示航迹
      if (this.map && !this.trackLayer) {
        try {
          if (typeof L !== 'undefined' && L.featureGroup) {
            this.trackLayer = L.featureGroup();
            this.trackLayer.addTo(this.map);
            this.trackLayer.setZIndex(999); // 设置为最高层级，置于所有图层最上面
          } else {
            console.error('Leaflet未正确加载或L.featureGroup不可用');
          }
        } catch (error) {
          console.error('创建轨迹图层时出错:', error);
        }
      }
    },
    
    removeTrackLayer() {
      // 移除航迹图层
      try {
        if (this.map && this.trackLayer) {
          console.log('移除航迹图层');
          
          // 先清空图层中的内容
          if (this.trackLayer.clearLayers) {
            this.trackLayer.clearLayers();
          }
          
          // 从地图中移除图层
          this.map.removeLayer(this.trackLayer);
          
          // 释放引用
          this.trackLayer = null;
        }
      } catch (error) {
        console.error('移除航迹图层时发生错误:', error);
        this.trackLayer = null;
      }
    },
    
    drawTrackOnMap() {
      try {
        // 检查Leaflet是否可用
        if (typeof L === 'undefined') {
          console.error('Leaflet未加载，无法绘制航迹');
          return;
        }

        // 清空现有航迹
        if (this.trackLayer) {
          this.trackLayer.clearLayers();
        }
        
        if (!this.trackPoints || this.trackPoints.length === 0) {
          return;
        }
        
        // 提取轨迹点坐标 - 确保按时间顺序排列
        const trackCoordinates = this.trackPoints.map(point => {
          return [point.latitude, point.longitude];
        });
        
        // 绘制航迹线
        if (trackCoordinates.length > 1) {
          // 创建航迹线
          const polyline = L.polyline(trackCoordinates, {
            color: '#3388FF',
            weight: 4,
            opacity: 0.8,
            smoothFactor: 1,
            dashArray: '8, 5', // 虚线效果
            lineCap: 'round',
            className: 'vessel-track-line'
          });
          
          // 添加航迹线装饰箭头 - 确保方向正确从早到晚
          if (L.polylineDecorator) {
            const decorator = L.polylineDecorator(polyline, {
              patterns: [
                {
                  offset: '5%', // 从起点偏移5%开始放置箭头
                  repeat: '10%', // 每隔10%放置一个箭头
                  symbol: L.Symbol.arrowHead({
                    pixelSize: 12, // 箭头大小
                    polygon: true, // 使用多边形绘制箭头
                    pathOptions: { // 箭头样式
                      stroke: true,
                      color: '#3388FF',
                      weight: 2,
                      fill: true,
                      fillColor: '#FFFFFF',
                      fillOpacity: 0.8
                    }
                  })
                }
              ]
            });
            
            this.trackLayer.addLayer(decorator);
          }
          
          this.trackLayer.addLayer(polyline);
          
          // 绘制航迹点信息标记
          this.drawTrackPointsInfoInline();
          
          // 绘制起始点和终点标记 - 明确区分时间早的是起点，晚的是终点
          const startPoint = trackCoordinates[0]; // 时间最早的点
          const endPoint = trackCoordinates[trackCoordinates.length - 1]; // 时间最晚的点
          
          // 起点标记（绿色）- 时间早的点
          L.circleMarker(startPoint, {
            radius: 7,
            fillColor: 'green',
            color: '#fff',
            weight: 2,
            opacity: 1,
            fillOpacity: 1
          }).addTo(this.trackLayer);
          
          // // 起点标签 - 显示最早的时间
          // const startLabel = L.marker(startPoint, {
          //   icon: L.divIcon({
          //     html: `<div class="track-point-label start-point">起点 (${this.formatTime(this.trackPoints[0].timestamp)})</div>`,
          //     className: 'track-label-start'
          //   }),
          //   zIndexOffset: 1000
          // });
          // startLabel.addTo(this.trackLayer);
          
          // 终点标记（红色）- 时间晚的点
          L.circleMarker(endPoint, {
            radius: 7,
            fillColor: 'red',
            color: '#fff',
            weight: 2,
            opacity: 1,
            fillOpacity: 1
          }).addTo(this.trackLayer);
          
          // // 终点标签 - 显示最晚的时间
          // const endLabel = L.marker(endPoint, {
          //   icon: L.divIcon({
          //     html: `<div class="track-point-label end-point">终点 (${this.formatTime(this.trackPoints[this.trackPoints.length - 1].timestamp)})</div>`,
          //     className: 'track-label-end'
          //   }),
          //   zIndexOffset: 1000
          // });
          // endLabel.addTo(this.trackLayer);
          
          // 调整地图视图以包含整个航迹
          this.map.fitBounds(polyline.getBounds(), { padding: [50, 50] });
        }
      } catch (error) {
        console.error('绘制航迹时发生错误:', error);
      }
    },
    
    /**
     * 智能采样轨迹点 - 基于速度和位移的优化
     */
    intelligentSampling() {
      if (!this.trackPoints || this.trackPoints.length === 0) {
        return [];
      }
      
      const sampledIndices = new Set();
      
      // 始终包含起点和终点
      sampledIndices.add(0);
      sampledIndices.add(this.trackPoints.length - 1);
      
      // 遍历所有点，根据速度和位移决定是否保留
      for (let i = 1; i < this.trackPoints.length - 1; i++) {
        const point = this.trackPoints[i];
        const prevPoint = this.trackPoints[i - 1];
        
        // 获取速度（节）
        const speed = point.speed !== null && point.speed !== undefined ? point.speed : 0;
        
        // 计算与上一个点的位移（米）
        const displacement = this.calculateDistance(
          prevPoint.latitude, prevPoint.longitude,
          point.latitude, point.longitude
        );
        
        // 采样率决策
        let samplingRate = 1.0; // 默认100%保留
        
        // 1. 静止或极低速（< 0.5节）：只保留10%
        if (speed < 0.5) {
          samplingRate = 0.1;
        }
        // 2. 低速（0.5 - 1节）：保留20%
        else if (speed < 1) {
          samplingRate = 0.2;
        }
        // 3. 慢速（1 - 2节）：保留30%
        else if (speed < 2) {
          samplingRate = 0.3;
        }
        // 4. 正常速度（>= 2节）：保留60%
        else {
          samplingRate = 0.6;
        }
        
        // 位移很小时进一步降低采样率
        if (displacement < 10) {
          samplingRate *= 0.5; // 位移<10米，采样率减半
        } else if (displacement < 30) {
          samplingRate *= 0.7; // 位移<30米，采样率降至70%
        }
        
        // 根据采样率决定是否保留该点
        if (Math.random() < samplingRate) {
          sampledIndices.add(i);
        }
      }
      
      // 确保采样后至少有一定数量的点（避免过度稀疏）
      const minPoints = Math.min(10, this.trackPoints.length);
      if (sampledIndices.size < minPoints) {
        // 如果采样点太少，补充一些均匀分布的点
        const step = Math.floor(this.trackPoints.length / minPoints);
        for (let i = 0; i < this.trackPoints.length; i += step) {
          sampledIndices.add(i);
          if (sampledIndices.size >= minPoints) break;
        }
      }
      
      // 转换为数组并排序
      const sortedIndices = Array.from(sampledIndices).sort((a, b) => a - b);
      const sampledPoints = sortedIndices.map(index => this.trackPoints[index]);
      
      console.log(`智能采样完成: 原始点数 ${this.trackPoints.length}, 采样后点数 ${sampledPoints.length}, 保留率 ${(sampledPoints.length / this.trackPoints.length * 100).toFixed(1)}%`);
      
      return sampledPoints;
    },
    
    /**
     * 计算两点之间的距离（米）
     */
    calculateDistance(lat1, lon1, lat2, lon2) {
      const R = 6371000; // 地球半径（米）
      const dLat = (lat2 - lat1) * Math.PI / 180;
      const dLon = (lon2 - lon1) * Math.PI / 180;
      const a = Math.sin(dLat / 2) * Math.sin(dLat / 2) +
                Math.cos(lat1 * Math.PI / 180) * Math.cos(lat2 * Math.PI / 180) *
                Math.sin(dLon / 2) * Math.sin(dLon / 2);
      const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
      return R * c;
    },
    
    /**
     * 处理地图缩放变化
     */
    handleMapZoomChange() {
      console.log('地图缩放级别变化，重新绘制轨迹点标签');
      
      // 只重绘轨迹点标签，不调用完整的drawTrackOnMap以避免重置地图视图
      if (this.trackPoints && this.trackPoints.length > 0 && this.trackLayer) {
        // 清除现有的标记（保留轨迹线）
        this.trackLayer.eachLayer((layer) => {
          // 只移除标记类型的图层，保留轨迹线和装饰器
          if (layer instanceof L.Marker || layer instanceof L.CircleMarker) {
            this.trackLayer.removeLayer(layer);
          }
        });
        
        // 重新绘制起点和终点标记
        const trackCoordinates = this.trackPoints.map(point => [point.latitude, point.longitude]);
        if (trackCoordinates.length > 0) {
          const startPoint = trackCoordinates[0];
          const endPoint = trackCoordinates[trackCoordinates.length - 1];
          
          // 起点标记（绿色）
          L.circleMarker(startPoint, {
            radius: 7,
            fillColor: 'green',
            color: '#fff',
            weight: 2,
            opacity: 1,
            fillOpacity: 1
          }).addTo(this.trackLayer);
          
          // 终点标记（红色）
          L.circleMarker(endPoint, {
            radius: 7,
            fillColor: 'red',
            color: '#fff',
            weight: 2,
            opacity: 1,
            fillOpacity: 1
          }).addTo(this.trackLayer);
        }
        
        // 重新绘制轨迹点信息标记（会根据当前缩放级别决定是否显示标签）
        this.drawTrackPointsInfoInline();
      }
    },
    
    /**
     * 绘制航迹点信息标记（内联版本）
     */
    drawTrackPointsInfoInline() {
      if (!this.trackPoints || this.trackPoints.length === 0) {
        return;
      }
      
      // 获取当前地图缩放级别
      const currentZoom = this.map ? this.map.getZoom() : 17;
      
      // 判断是否显示标签：完全由 forceShowLabels 控制（默认为 false，不显示）
      const showLabels = this.forceShowLabels;
      
      console.log(`当前地图缩放级别: ${currentZoom}, ${showLabels ? '显示' : '隐藏'}标签`);
      
      // 使用智能采样替代原有的简单采样
      const sampledPoints = this.intelligentSampling();
      
      sampledPoints.forEach((point, index) => {
        const speed = point.speed !== null && point.speed !== undefined ? `${point.speed}节` : '-';
        const heading = point.heading !== null && point.heading !== undefined && point.heading < 360 ? `${point.heading}°` : '-';
        const time = this.formatSimpleTime(point.timestamp);
        
        // 创建航迹点信息标记，使用唯一的className
        const uniqueClassName = `track-point-info-${index}-${Date.now()}`;
        
        // 先添加圆点标记
        const circleMarker = L.circleMarker([point.latitude, point.longitude], {
          radius: 4,
          fillColor: '#007bff',
          color: '#ffffff',
          weight: 2,
          opacity: 1,
          fillOpacity: 0.8,
          className: 'track-data-point'
        });
        
        // // 添加圆点的点击事件
        // circleMarker.on('click', () => {
        //   this.showPointDetailDialog(point, `航迹点 ${index + 1}`);
        // });
        
        // // 添加圆点的悬停效果
        // circleMarker.on('mouseover', () => {
        //   circleMarker.setStyle({
        //     radius: 6,
        //     fillOpacity: 1,
        //     color: '#409EFF'
        //   });
        // });
        
        circleMarker.on('mouseout', () => {
          circleMarker.setStyle({
            radius: 4,
            fillOpacity: 0.8,
            color: '#ffffff'
          });
        });
        
        // 添加圆点到图层
        circleMarker.addTo(this.trackLayer);
        
        // 只有在缩放级别 > 15 时才添加信息标签
        if (showLabels) {
          const infoMarker = L.marker([point.latitude, point.longitude], {
            icon: L.divIcon({
              html: `
                <div class="track-point-info-label">
                  ${time} | ⚡${speed} | 🧭${heading}
                </div>
              `,
              className: uniqueClassName,
              iconSize: [160, 24],
              iconAnchor: [80, 12]
            }),
            zIndexOffset: 1000
          });
          
          // // 添加信息标记的点击事件
          // infoMarker.on('click', () => {
          //   this.showPointDetailDialog(point, `航迹点 ${index + 1}`);
          // });
          
          // 添加信息标记到图层
          infoMarker.addTo(this.trackLayer);
        }
      });
    },
    
    /**
     * 绘制航迹点信息标记（原方法保留作备份）
     */
    drawTrackPointsInfo() {
      if (!this.trackPoints || this.trackPoints.length === 0) {
        return;
      }
      
      // 对所有轨迹点进行采样，避免标记过多影响性能
      let sampledPoints = this.trackPoints;
      if (this.trackPoints.length > 15) {
        // 采样最多15个点
        const step = Math.floor(this.trackPoints.length / 15);
        sampledPoints = this.trackPoints.filter((point, index) => index % step === 0);
      }
      
      sampledPoints.forEach((point, index) => {
        const speed = point.speed !== null && point.speed !== undefined ? `${point.speed}节` : '-';
        const heading = point.heading !== null && point.heading !== undefined && point.heading < 360 ? `${point.heading}°` : '-';
        const time = this.formatSimpleTime(point.timestamp);
        
        // 创建航迹点信息标记，使用唯一的className
        const uniqueClassName = `track-point-info-${index}-${Date.now()}`;
        
        // 先添加圆点标记
        const circleMarker = L.circleMarker([point.latitude, point.longitude], {
          radius: 4,
          fillColor: '#007bff',
          color: '#ffffff',
          weight: 2,
          opacity: 1,
          fillOpacity: 0.8,
          className: 'track-data-point'
        });
        
        // // 添加圆点的点击事件
        // circleMarker.on('click', () => {
        //   this.showPointDetailDialog(point, `航迹点 ${index + 1}`);
        // });
        
        // 添加圆点的悬停效果
        circleMarker.on('mouseover', () => {
          circleMarker.setStyle({
            radius: 6,
            fillOpacity: 1,
            color: '#409EFF'
          });
        });
        
        circleMarker.on('mouseout', () => {
          circleMarker.setStyle({
            radius: 4,
            fillOpacity: 0.8,
            color: '#ffffff'
          });
        });
        
        // 添加圆点到图层
        circleMarker.addTo(this.trackLayer);
        
        // 再添加信息标记
        const infoMarker = L.marker([point.latitude, point.longitude], {
          icon: L.divIcon({
            html: `
              <div class="track-point-info-label">
                ${time} | ⚡${speed} | 🧭${heading}
              </div>
            `,
            className: uniqueClassName,
            iconSize: [160, 24],
            iconAnchor: [80, 12]
          }),
          zIndexOffset: 1000
        });
        
        // // 添加信息标记的点击事件
        // infoMarker.on('click', () => {
        //   this.showPointDetailDialog(point, `航迹点 ${index + 1}`);
        // });
        
        // 添加信息标记到图层
        infoMarker.addTo(this.trackLayer);
      });
    },
    
    /**
     * 显示航迹点详细信息对话框
     */
    showPointDetailDialog(point, pointType = '航迹点') {
      const time = this.formatTime(point.timestamp);
      const speed = point.speed !== null && point.speed !== undefined ? point.speed : '无数据';
      const heading = point.heading !== null && point.heading !== undefined && point.heading < 360 ? point.heading : '无数据';
      const latitude = point.latitude ? point.latitude.toFixed(6) : '无数据';
      const longitude = point.longitude ? point.longitude.toFixed(6) : '无数据';
      const vesselName = point.vesselName || this.mmsi;
      const vesselType = point.vesselType || '未知';
      const isWarning = point.isWarning ? '是' : '否';
      const warningType = point.warningType || '无';
      
      const detailContent = `
        <div class="track-point-detail">
          <div class="detail-section">
            <h4><i class="el-icon-ship"></i> 船舶信息</h4>
            <div class="detail-row">
              <span class="label">船舶名称:</span>
              <span class="value">${vesselName}</span>
            </div>
            <div class="detail-row">
              <span class="label">MMSI:</span>
              <span class="value">${point.mmsi || this.mmsi}</span>
            </div>
            <div class="detail-row">
              <span class="label">船舶类型:</span>
              <span class="value">${vesselType}</span>
            </div>
          </div>
          
          <div class="detail-section">
            <h4><i class="el-icon-time"></i> 时间信息</h4>
            <div class="detail-row">
              <span class="label">记录时间:</span>
              <span class="value">${time}</span>
            </div>
          </div>
          
          <div class="detail-section">
            <h4><i class="el-icon-location"></i> 位置信息</h4>
            <div class="detail-row">
              <span class="label">经度:</span>
              <span class="value">${longitude}</span>
            </div>
            <div class="detail-row">
              <span class="label">纬度:</span>
              <span class="value">${latitude}</span>
            </div>
          </div>
          
          <div class="detail-section">
            <h4><i class="el-icon-speedometer"></i> 航行信息</h4>
            <div class="detail-row">
              <span class="label">航速:</span>
              <span class="value">${speed === '无数据' ? speed : speed + ' 节'}</span>
            </div>
            <div class="detail-row">
              <span class="label">航向:</span>
              <span class="value">${heading === '无数据' ? heading : heading + '°'}</span>
            </div>
          </div>
          
          <div class="detail-section">
            <h4><i class="el-icon-warning"></i> 告警信息</h4>
            <div class="detail-row">
              <span class="label">是否告警:</span>
              <span class="value ${point.isWarning ? 'warning-yes' : 'warning-no'}">${isWarning}</span>
            </div>
            ${point.isWarning ? `
            <div class="detail-row">
              <span class="label">告警类型:</span>
              <span class="value warning-type">${warningType}</span>
            </div>
            ` : ''}
          </div>
        </div>
      `;
      
      // 使用ElementUI的MessageBox显示详细信息
      this.$msgbox({
        title: `${pointType} - ${this.shipName}`,
        message: detailContent,
        dangerouslyUseHTMLString: true,
        customClass: 'track-point-detail-dialog',
        showCancelButton: false,
        confirmButtonText: '关闭',
        beforeClose: (action, instance, done) => {
          done();
        }
      }).catch(() => {
        // 用户取消操作，不需要处理
      });
    },
    
    // 新增：切换显示数据表格
    toggleDataTable() {
      this.showDataTable = !this.showDataTable;
      // 重置分页参数
      if (this.showDataTable) {
        this.dataTableParams = {
          pageNum: 1,
          pageSize: 10
        };
      }
    },
    
    // 新增：切换显示标签
    toggleLabels() {
      this.forceShowLabels = !this.forceShowLabels;
      console.log(`手动切换标签显示: ${this.forceShowLabels ? '显示' : '隐藏'}`);
      
      // 重新绘制轨迹点标签
      if (this.trackPoints && this.trackPoints.length > 0 && this.trackLayer) {
        // 清除现有的标记（保留轨迹线）
        this.trackLayer.eachLayer((layer) => {
          // 只移除标记类型的图层，保留轨迹线和装饰器
          if (layer instanceof L.Marker || layer instanceof L.CircleMarker) {
            this.trackLayer.removeLayer(layer);
          }
        });
        
        // 重新绘制起点和终点标记
        const trackCoordinates = this.trackPoints.map(point => [point.latitude, point.longitude]);
        if (trackCoordinates.length > 0) {
          const startPoint = trackCoordinates[0];
          const endPoint = trackCoordinates[trackCoordinates.length - 1];
          
          // 起点标记（绿色）
          L.circleMarker(startPoint, {
            radius: 7,
            fillColor: 'green',
            color: '#fff',
            weight: 2,
            opacity: 1,
            fillOpacity: 1
          }).addTo(this.trackLayer);
          
          // 终点标记（红色）
          L.circleMarker(endPoint, {
            radius: 7,
            fillColor: 'red',
            color: '#fff',
            weight: 2,
            opacity: 1,
            fillOpacity: 1
          }).addTo(this.trackLayer);
        }
        
        // 重新绘制轨迹点信息标记
        this.drawTrackPointsInfoInline();
      }
    },
    
    // 新增：定位船舶功能
    locateShip() {
      // 如果有最新位置信息，则定位到该船舶
      if (this.latestShipPosition) {
        // 先在地图上设置中心点
        this.map.setView([this.latestShipPosition.lat, this.latestShipPosition.lng], 16);
        
        // 然后通知父组件，以便父组件可以高亮显示或者做其他处理
        this.$emit('locate-ship', {
          mmsi: this.mmsi,
          position: this.latestShipPosition
        });
        
        this.$message.success(`已定位到船舶 ${this.shipName}`);
      } else if (this.trackPoints && this.trackPoints.length > 0) {
        // 如果没有保存最新位置但有轨迹点，则使用最后一个点
        const latestPoint = this.trackPoints[this.trackPoints.length - 1];
        const position = {
          lat: latestPoint.latitude,
          lng: latestPoint.longitude
        };
        
        this.map.setView([position.lat, position.lng], 16);
        this.$emit('locate-ship', {
          mmsi: this.mmsi,
          position: position
        });
        
        this.$message.success(`已定位到船舶 ${this.shipName}`);
      } else {
        this.$message.warning('无法定位船舶，没有位置信息');
      }
    },
    
    // 处理数据表格分页变化
    handlePaginationChange(params) {
      this.dataTableParams = {
        ...this.dataTableParams,
        ...params
      };
    },
    
    closeViewer() {
      this.removeTrackLayer()
      this.$emit('close')
      this.$emit('update:visible', false)
    },
    
    onTimeRangeChanged() {
      // 时间范围变化时重新加载数据
      this.loadTrackData()
    },
    
    formatTime(timestamp) {
      if (!timestamp) return ''
      const date = new Date(timestamp)
      return this.formatDateTime(date)
    },
    
    formatDateTime(date) {
      const year = date.getFullYear();
      const month = String(date.getMonth() + 1).padStart(2, '0');
      const day = String(date.getDate()).padStart(2, '0');
      const hour = String(date.getHours()).padStart(2, '0');
      const minute = String(date.getMinutes()).padStart(2, '0');
      const second = String(date.getSeconds()).padStart(2, '0');
      
      // 返回API期望的标准日期时间格式
      return `${year}-${month}-${day} ${hour}:${minute}:${second}`;
    },
    
    /**
     * 格式化简化时间（仅显示时分）
     */
    formatSimpleTime(timestamp) {
      if (!timestamp) return '';
      const date = new Date(timestamp);
      const month = String(date.getMonth() + 1).padStart(2, '0');
      const day = String(date.getDate()).padStart(2, '0');
      const hour = String(date.getHours()).padStart(2, '0');
      const minute = String(date.getMinutes()).padStart(2, '0');
      const second = String(date.getSeconds()).padStart(2, '0');
      
      // 返回简化的时间格式：月-日 时:分
      return `${month}-${day} ${hour}:${minute}:${second}`;
    }
  },
  watch: {
    mmsi: {
      handler(newMmsi, oldMmsi) {
        console.log(`船舶MMSI变更: ${oldMmsi} -> ${newMmsi}`);
        if (newMmsi && newMmsi !== oldMmsi) {
          // 设置船舶名称
          this.shipName = newMmsi;
          
          console.log(`准备加载船舶 ${newMmsi} 的航迹数据, 时间范围:`, this.localTimeRange);
          
          // 只有在有有效时间范围或mounted后才执行API调用
          // 这样避免在组件初始化时因时间范围未传递而使用默认时间范围
          if (this.localTimeRange && this.localTimeRange.length === 2) {
            this.loadTrackData();
          } else {
            console.log('时间范围未设置，等待timeRange prop传递或mounted钩子执行');
          }
        }
      },
      immediate: true
    },
    timeRange: {
      handler(newRange) {
        if (newRange && Array.isArray(newRange) && newRange.length === 2) {
          this.localTimeRange = [...newRange];
          console.log('从props接收到有效时间范围:', this.localTimeRange);
          
          // 如果MMSI已设置，则触发数据加载
          if (this.mmsi && this.mmsi.trim() !== '') {
            console.log('时间范围已设置，开始加载轨迹数据');
            this.loadTrackData();
          }
        } else if (newRange !== undefined) {
          console.log('从props接收到无效时间范围:', newRange, '将使用默认时间范围');
        }
      },
      immediate: true
    }
  }
}
</script>

<style scoped>
.track-viewer-container {
  display: flex;
  flex-direction: column;
  height: 100%;
}

.track-viewer-toolbar {
  padding: 10px 15px;
  border-bottom: 1px solid #ebeef5;
  display: flex;
  gap: 8px;
  flex-wrap: wrap;
  align-items: center;
}

.track-viewer-content {
  padding: 15px;
  max-height: 500px;
  overflow-y: auto;
  overflow-x: hidden;
}

.loading, .empty-data {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  height: 100px;
  color: #909399;
}

.loading i, .empty-data i {
  font-size: 32px;
  margin-bottom: 10px;
}

.track-summary {
  display: flex;
  justify-content: space-between;
  flex-wrap: wrap;
}

.summary-item {
  margin-right: 15px;
  margin-bottom: 8px;
}

.summary-item .label {
  font-weight: 500;
  color: #606266;
  margin-right: 5px;
}

.summary-item .value {
  font-weight: bold;
  color: #303133;
}

.data-table-container {
  margin-top: 15px;
  border-top: 1px solid #ebeef5;
  padding-top: 15px;
  width: 100%;
  overflow-x: hidden;
}
</style>

<style>
/* 全局样式 */
.vessel-track-line {
  animation: dash 5s linear infinite;
}

@keyframes dash {
  to {
    stroke-dashoffset: -20;
  }
}

.track-point-label {
  background-color: rgba(0, 0, 0, 0.6);
  color: white;
  padding: 2px 6px;
  border-radius: 3px;
  font-size: 12px;
  white-space: nowrap;
  pointer-events: none;
}

.start-point {
  background-color: rgba(40, 167, 69, 0.8);
}

.end-point {
  background-color: rgba(220, 53, 69, 0.8);
}

/* 移除旧的箭头样式 */
.track-label-start, .track-label-end {
  background: transparent;
  border: none;
}

/* 航迹点信息标记样式 */
.track-point-info-label {
  width: 200px;
  background-color: rgba(255, 255, 255, 0.9);
  color: #333333;
  padding: 3px 8px;
  border-radius: 4px;
  font-size: 11px;
  font-weight: 600;
  white-space: nowrap;
  pointer-events: auto;
  cursor: pointer;
  border: 1px solid rgba(0, 0, 0, 0.1);
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.15);
  transition: all 0.2s ease;
  text-align: center;
  line-height: 1.2;
  backdrop-filter: blur(4px);
  z-index: 1000;
  position: relative;
}

.track-point-info-label:hover {
  background-color: rgba(64, 158, 255, 0.95);
  color: white;
  transform: scale(1.08);
  box-shadow: 0 4px 12px rgba(64, 158, 255, 0.3);
  border-color: rgba(64, 158, 255, 0.5);
}

/* 航迹数据点圆点样式 */
.track-data-point {
  cursor: pointer;
  transition: all 0.2s ease;
  z-index: 500;
}

.track-data-point:hover {
  transform: scale(1.3);
}

/* 详细信息弹窗样式 */
.track-point-detail {
  font-size: 14px;
  line-height: 1.6;
  max-height: 500px;
  overflow-y: auto;
}

.track-point-detail .detail-section {
  margin-bottom: 20px;
  padding-bottom: 15px;
  border-bottom: 1px solid #ebeef5;
}

.track-point-detail .detail-section:last-child {
  border-bottom: none;
  margin-bottom: 0;
}

.track-point-detail h4 {
  margin: 0 0 10px 0;
  color: #409EFF;
  font-size: 16px;
  font-weight: 600;
  display: flex;
  align-items: center;
}

.track-point-detail h4 i {
  margin-right: 8px;
  font-size: 18px;
}

.track-point-detail .detail-row {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 8px;
  padding: 4px 0;
}

.track-point-detail .detail-row:last-child {
  margin-bottom: 0;
}

.track-point-detail .label {
  font-weight: 500;
  color: #606266;
  flex-shrink: 0;
  width: 80px;
}

.track-point-detail .value {
  color: #303133;
  font-weight: 600;
  text-align: right;
  flex: 1;
}

.track-point-detail .warning-yes {
  color: #F56C6C;
}

.track-point-detail .warning-no {
  color: #67C23A;
}

.track-point-detail .warning-type {
  color: #E6A23C;
}

/* 全局弹窗样式 */
.track-point-detail-dialog {
  max-width: 600px;
}

.track-point-detail-dialog .el-message-box__content {
  padding: 20px !important;
}

.track-point-detail-dialog .el-message-box__title {
  color: #409EFF;
  font-weight: 600;
}
</style>
